import { zodResolver } from '@hookform/resolvers/zod';
import { useQueryClient } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { type CreateWorkspaceInput, createWorkspaceSchema } from '@/lib/schemas';
import { supabase } from '@/lib/supabase/client';
import { workspacesKeys } from '@/lib/supabase/queries/workspaces';

export const Route = createFileRoute('/_app/onboarding')({
  component: OnboardingPage,
});

function slugify(input: string): string {
  return (
    input
      .toLowerCase()
      .normalize('NFD')
      // biome-ignore lint/suspicious/noMisleadingCharacterClass: intentional Unicode diacritic strip range
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 40)
  );
}

function OnboardingPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);

  const form = useForm<CreateWorkspaceInput>({
    resolver: zodResolver(createWorkspaceSchema),
    defaultValues: { name: '', slug: '' },
  });

  const onSubmit = form.handleSubmit(async (values) => {
    setSubmitting(true);
    try {
      const { data: userData } = await supabase.auth.getUser();
      const userId = userData.user?.id;
      if (!userId) throw new Error('No hay sesion activa'); // i18n

      const { data, error } = await supabase
        .from('workspaces')
        .insert({
          name: values.name,
          slug: values.slug,
          owner_id: userId,
        })
        .select('slug')
        .single();

      if (error) throw error;

      // El switcher y los listados leen de queries cacheadas con staleTime 5min.
      // Sin invalidar, el workspace recien creado no aparece hasta un F5 (que limpia
      // el cache). Invalidamos el prefijo 'workspaces' para forzar refetch.
      await queryClient.invalidateQueries({ queryKey: workspacesKeys.all });

      toast.success('Workspace creado'); // i18n
      if (data?.slug) {
        await navigate({ to: '/$wsSlug/projects', params: { wsSlug: data.slug } });
      } else {
        await navigate({ to: '/' });
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : 'No se pudo crear el workspace';
      toast.error(message); // i18n
    } finally {
      setSubmitting(false);
    }
  });

  // Auto-slug: al cambiar el nombre, si el slug esta vacio (o fue auto), genera.
  function onNameChange(e: React.ChangeEvent<HTMLInputElement>) {
    form.setValue('name', e.target.value, { shouldValidate: true });
    if (!form.getFieldState('slug').isDirty) {
      form.setValue('slug', slugify(e.target.value), { shouldValidate: true });
    }
  }

  return (
    <div className="mx-auto max-w-lg p-8">
      <div className="mb-6">
        {/* i18n */}
        <h1 className="text-2xl font-semibold text-ink">Crea tu primer workspace</h1>
        <p className="mt-2 text-sm text-quiet">
          Un workspace agrupa a tu equipo, proyectos y ciclos.
        </p>
      </div>

      <form className="flex flex-col gap-4" onSubmit={onSubmit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ws-name">Nombre</Label>
          <Input
            id="ws-name"
            type="text"
            placeholder="Acme Inc."
            value={form.watch('name')}
            onChange={onNameChange}
          />
          {form.formState.errors.name ? (
            <span className="text-xs text-critical">{form.formState.errors.name.message}</span>
          ) : null}
        </div>

        <div className="flex flex-col gap-1.5">
          <Label htmlFor="ws-slug">Slug</Label>
          <Input id="ws-slug" type="text" placeholder="acme" {...form.register('slug')} />
          <span className="text-xs text-quiet">
            {/* i18n */}
            Se usara en la URL: meidei.app/<strong>{form.watch('slug') || 'slug'}</strong>
          </span>
          {form.formState.errors.slug ? (
            <span className="text-xs text-critical">{form.formState.errors.slug.message}</span>
          ) : null}
        </div>

        <Button type="submit" disabled={submitting} className="mt-2">
          {submitting ? 'Creando...' : 'Crear workspace'}
        </Button>
      </form>
    </div>
  );
}
